import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Card,
  Steps,
  Button,
  Form,
  Input,
  Result,
  Spin,
  Typography,
  Divider,
  App,
  Alert,
} from 'antd';
import {
  ShoppingOutlined,
  UserOutlined,
  CreditCardOutlined,
  ArrowLeftOutlined,
  LoginOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { useCart, CartSummary, CheckoutForm as CartCheckoutForm } from '@digiwedge/ui-axiz';
import { createOrder } from '@digiwedge/axiz_api';
import { useGlobalStore, type GlobalState } from '@digiwedge/global-state';

const { Title, Text } = Typography;

type CheckoutStep = 'cart' | 'details' | 'payment';

interface CustomerDetails {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  postalCode: string;
}

export function CheckoutPage() {
  const [currentStep, setCurrentStep] = useState<CheckoutStep>('cart');
  const [customerDetails, setCustomerDetails] = useState<CustomerDetails | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderId, setOrderId] = useState<string | null>(null);
  const { items, clear } = useCart();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { message } = App.useApp();

  // Authentication state
  const isAuthenticated = useGlobalStore((s: GlobalState) => s.isAuthenticated);
  const profile = useGlobalStore((s: GlobalState) => s.profile);

  // Pre-fill form for authenticated users
  useEffect(() => {
    if (isAuthenticated && profile && !customerDetails) {
      const nameParts = (profile.displayName || profile.email?.split('@')[0] || '').split(' ');
      const firstName = profile.givenName || nameParts[0] || '';
      const lastName = profile.familyName || nameParts.slice(1).join(' ') || '';
      form.setFieldsValue({
        firstName,
        lastName,
        email: profile.email,
        phone: profile.phoneNumber || '',
      });
    }
  }, [isAuthenticated, profile, customerDetails, form]);

  const stepIndex = { cart: 0, details: 1, payment: 2 }[currentStep];

  const total = items.reduce((sum, item) => sum + item.product.price * item.quantity, 0);

  const handleDetailsSubmit = async (values: CustomerDetails) => {
    setCustomerDetails(values);
    setCurrentStep('payment');
  };

  const handlePaymentComplete = async () => {
    if (!customerDetails) return;

    setIsProcessing(true);
    try {
      const order = await createOrder({
        reference: `WEB-${Date.now()}`,
        orderDate: new Date().toISOString(),
        totalAmount: total,
        status: 'New',
        // Link order to authenticated user for order history
        userId: isAuthenticated && profile?.userId ? profile.userId : undefined,
        customerEmail: customerDetails.email,
        customerName: `${customerDetails.firstName} ${customerDetails.lastName}`,
        phoneNumber: customerDetails.phone,
        shippingAddress: customerDetails.address,
        shippingCity: customerDetails.city,
        shippingPostalCode: customerDetails.postalCode,
        items: items.map((item) => ({
          productId: item.product.productIdentifier,
          productName: item.product.name,
          quantity: item.quantity,
          unitPrice: item.product.price,
        })),
      });
      setOrderId(order.id ?? order.reference);
      clear();
      navigate(`/order-confirmation/${order.id ?? order.reference}`);
    } catch (error) {
      message.error('Failed to process order. Please try again.');
      console.error('Order creation failed:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  if (items.length === 0 && !orderId) {
    return (
      <Result
        icon={<ShoppingOutlined style={{ color: '#1890ff' }} />}
        title="Your cart is empty"
        subTitle="Add some products to your cart to proceed with checkout."
        extra={
          <Link to="/">
            <Button type="primary" icon={<ArrowLeftOutlined />}>
              Continue Shopping
            </Button>
          </Link>
        }
      />
    );
  }

  return (
    <div className="checkout-page" style={{ maxWidth: 900, margin: '0 auto' }}>
      <Title level={2}>Checkout</Title>

      <Steps
        current={stepIndex}
        style={{ marginBottom: 32 }}
        items={[
          { title: 'Cart Review', icon: <ShoppingOutlined /> },
          { title: 'Your Details', icon: <UserOutlined /> },
          { title: 'Payment', icon: <CreditCardOutlined /> },
        ]}
      />

      {currentStep === 'cart' && (
        <Card>
          <Title level={4}>Review Your Cart</Title>
          <CartCheckoutForm onCheckout={() => setCurrentStep('details')} />
          <Divider />

          {/* Prompt guests to sign in for order history */}
          {!isAuthenticated && (
            <Alert
              message="Sign in for a better experience"
              description="Create an account or sign in to track your orders, save addresses, and check out faster next time."
              type="info"
              showIcon
              icon={<LoginOutlined />}
              style={{ marginBottom: 16 }}
              action={
                <Link to="/login" state={{ from: '/checkout' }}>
                  <Button size="small" type="primary">
                    Sign In
                  </Button>
                </Link>
              }
            />
          )}

          {isAuthenticated && profile && (
            <Alert
              message={`Welcome back, ${profile.givenName || profile.displayName || 'valued customer'}!`}
              description="Your order will be saved to your account for easy tracking."
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
              style={{ marginBottom: 16 }}
            />
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Link to="/">
              <Button icon={<ArrowLeftOutlined />}>Continue Shopping</Button>
            </Link>
            <Button type="primary" onClick={() => setCurrentStep('details')}>
              Proceed to Details
            </Button>
          </div>
        </Card>
      )}

      {currentStep === 'details' && (
        <Card>
          <Title level={4}>Your Details</Title>

          {isAuthenticated && (
            <Alert
              message="Your details have been pre-filled from your account"
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <Form
            form={form}
            layout="vertical"
            onFinish={handleDetailsSubmit}
            initialValues={customerDetails ?? undefined}
          >
            <div style={{ display: 'flex', gap: 16 }}>
              <Form.Item
                name="firstName"
                label="First Name"
                rules={[{ required: true, message: 'Please enter your first name' }]}
                style={{ flex: 1 }}
              >
                <Input placeholder="John" />
              </Form.Item>
              <Form.Item
                name="lastName"
                label="Last Name"
                rules={[{ required: true, message: 'Please enter your last name' }]}
                style={{ flex: 1 }}
              >
                <Input placeholder="Doe" />
              </Form.Item>
            </div>
            <div style={{ display: 'flex', gap: 16 }}>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Please enter your email' },
                  { type: 'email', message: 'Please enter a valid email' },
                ]}
                style={{ flex: 1 }}
              >
                <Input placeholder="john@example.com" />
              </Form.Item>
              <Form.Item
                name="phone"
                label="Phone"
                rules={[{ required: true, message: 'Please enter your phone number' }]}
                style={{ flex: 1 }}
              >
                <Input placeholder="+27 12 345 6789" />
              </Form.Item>
            </div>
            <Form.Item
              name="address"
              label="Address"
              rules={[{ required: true, message: 'Please enter your address' }]}
            >
              <Input placeholder="123 Main Street" />
            </Form.Item>
            <div style={{ display: 'flex', gap: 16 }}>
              <Form.Item
                name="city"
                label="City"
                rules={[{ required: true, message: 'Please enter your city' }]}
                style={{ flex: 1 }}
              >
                <Input placeholder="Cape Town" />
              </Form.Item>
              <Form.Item
                name="postalCode"
                label="Postal Code"
                rules={[{ required: true, message: 'Please enter your postal code' }]}
                style={{ flex: 1 }}
              >
                <Input placeholder="8001" />
              </Form.Item>
            </div>
            <Divider />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button onClick={() => setCurrentStep('cart')}>Back to Cart</Button>
              <Button type="primary" htmlType="submit">
                Continue to Payment
              </Button>
            </div>
          </Form>
        </Card>
      )}

      {currentStep === 'payment' && (
        <Card>
          <Title level={4}>Payment</Title>
          <div style={{ marginBottom: 24 }}>
            <Text strong>Order Summary</Text>
            <CartSummary />
            <Divider />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text strong>Total:</Text>
              <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
                R {total.toFixed(2)}
              </Title>
            </div>
          </div>
          <Divider />
          {isProcessing ? (
            <div style={{ textAlign: 'center', padding: 32 }}>
              <Spin size="large" />
              <Text style={{ display: 'block', marginTop: 16 }}>Processing your order...</Text>
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button onClick={() => setCurrentStep('details')}>Back to Details</Button>
              <Button type="primary" onClick={handlePaymentComplete}>
                Complete Purchase
              </Button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

export default CheckoutPage;
